import React from 'react';
import {
  Box,
  Typography,
  Chip,
  Tooltip,
  useTheme,
  alpha
} from '@mui/material';
import {
  AccessTime as HourlyIcon,
  Today as DailyIcon,
  DateRange as WeeklyIcon,
  CalendarMonth as MonthlyIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useCurrency } from '../../context/CurrencyContext';

/**
 * Composant pour afficher les prix multi-périodes de manière élégante
 */
const PriceDisplay = ({ 
  listing, 
  variant = 'compact', // 'compact', 'detailed', 'inline'
  showCurrency = true,
  showIcon = true,
  primaryPeriod = 'auto' // 'auto', 'hourly', 'daily', 'weekly', 'monthly'
}) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const { formatPrice } = useCurrency();

  // Extraire les prix disponibles
  const prices = [
    {
      period: 'hourly',
      value: listing.price_hourly,
      icon: <HourlyIcon sx={{ fontSize: 14 }} />,
      label: t('listing.price.hourly', '/h'),
      shortLabel: t('listing.price.hourly.short', 'h'),
      priority: 1
    },
    {
      period: 'daily',
      value: listing.price_daily,
      icon: <DailyIcon sx={{ fontSize: 14 }} />,
      label: t('listing.price.daily', '/jour'),
      shortLabel: t('listing.price.daily.short', 'j'),
      priority: 2
    },
    {
      period: 'weekly',
      value: listing.price_weekly,
      icon: <WeeklyIcon sx={{ fontSize: 14 }} />,
      label: t('listing.price.weekly', '/semaine'),
      shortLabel: t('listing.price.weekly.short', 'sem'),
      priority: 3
    },
    {
      period: 'monthly',
      value: listing.price_monthly,
      icon: <MonthlyIcon sx={{ fontSize: 14 }} />,
      label: t('listing.price.monthly', '/mois'),
      shortLabel: t('listing.price.monthly.short', 'mois'),
      priority: 4
    }
  ].filter(price => price.value && price.value > 0);

  // Si aucun prix n'est disponible
  if (prices.length === 0) {
    return (
      <Typography 
        variant="body2" 
        color="text.secondary" 
        sx={{ fontStyle: 'italic' }}
      >
        {t('listing.price.contact', 'Prix sur demande')}
      </Typography>
    );
  }

  // Déterminer le prix principal à afficher
  let primaryPrice = prices[0];
  if (primaryPeriod !== 'auto') {
    const specifiedPrice = prices.find(p => p.period === primaryPeriod);
    if (specifiedPrice) {
      primaryPrice = specifiedPrice;
    }
  }

  // Formatage de la devise
  const formatCurrency = (amount) => {
    if (!showCurrency) return amount.toString();
    return formatPrice(amount, listing.currency || 'EUR');
  };

  // Mode compact - Affiche uniquement le prix principal avec espacement amélioré
  if (variant === 'compact') {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, minHeight: '24px' }}>
        {showIcon && (
          <Box sx={{ display: 'flex', alignItems: 'center', color: 'primary.main' }}>
            {primaryPrice.icon}
          </Box>
        )}
        <Typography 
          variant="subtitle2" 
          fontWeight={600} 
          color="primary.main"
          sx={{ lineHeight: 1.2 }}
        >
          {formatCurrency(primaryPrice.value)}
          <Typography 
            component="span" 
            variant="caption" 
            color="text.secondary" 
            sx={{ ml: 0.8, fontWeight: 400 }}
          >
            {primaryPrice.shortLabel}
          </Typography>
        </Typography>
        
        {/* Tooltip avec tous les prix disponibles */}
        {prices.length > 1 && (
          <Tooltip
            title={
              <Box sx={{ p: 0.5 }}>
                <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                  {t('listing.price.allPrices', 'Tous les tarifs')}
                </Typography>
                {prices.map((price, index) => (
                  <Box 
                    key={price.period}
                    sx={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      gap: 2,
                      mb: index < prices.length - 1 ? 0.8 : 0,
                      py: 0.2
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      {price.icon}
                      <Typography variant="body2">
                        {t(`listing.period.${price.period}`, price.label)}
                      </Typography>
                    </Box>
                    <Typography variant="body2" fontWeight={600}>
                      {formatCurrency(price.value)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            }
            arrow
            placement="top"
          >
            <Typography 
              variant="caption" 
              color="primary.main" 
              sx={{ 
                cursor: 'help',
                textDecoration: 'underline dotted',
                ml: 0.5,
                fontWeight: 500,
                '&:hover': {
                  color: 'primary.dark'
                }
              }}
            >
              +{prices.length - 1}
            </Typography>
          </Tooltip>
        )}
      </Box>
    );
  }

  // Mode inline - Affiche tous les prix en une ligne avec espacement amélioré
  if (variant === 'inline') {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {prices.map((price, index) => (
          <Chip
            key={price.period}
            size="small"
            icon={showIcon ? price.icon : undefined}
            label={`${formatCurrency(price.value)} ${price.shortLabel}`}
            variant={index === 0 ? 'filled' : 'outlined'}
            color={index === 0 ? 'primary' : 'default'}
            sx={{
              fontSize: '0.75rem',
              height: 28,
              minWidth: '80px',
              '& .MuiChip-label': {
                fontWeight: index === 0 ? 600 : 400,
                px: 1
              },
              '& .MuiChip-icon': {
                marginLeft: 0.5
              }
            }}
          />
        ))}
      </Box>
    );
  }

  // Mode detailed - Affiche tous les prix dans une liste verticale
  if (variant === 'detailed') {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Typography variant="subtitle2" fontWeight={600} gutterBottom>
          {t('listing.price.title', 'Tarifs')}
        </Typography>
        
        {prices.map((price, index) => (
          <Box 
            key={price.period}
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center',
              py: 0.5,
              px: 1,
              borderRadius: 1,
              backgroundColor: index === 0 
                ? alpha(theme.palette.primary.main, 0.08)
                : 'transparent',
              border: index === 0 
                ? `1px solid ${alpha(theme.palette.primary.main, 0.2)}`
                : '1px solid transparent'
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {showIcon && price.icon}
              <Typography 
                variant="body2" 
                fontWeight={index === 0 ? 600 : 400}
                color={index === 0 ? 'primary.main' : 'text.primary'}
              >
                {t(`listing.period.${price.period}`, price.label)}
              </Typography>
            </Box>
            
            <Typography 
              variant="body2" 
              fontWeight={600}
              color={index === 0 ? 'primary.main' : 'text.primary'}
            >
              {formatCurrency(price.value)}
            </Typography>
          </Box>
        ))}

        {/* Affichage de la caution si disponible */}
        {listing.deposit_amount && listing.deposit_amount > 0 && (
          <Box sx={{ 
            mt: 1, 
            pt: 1, 
            borderTop: `1px solid ${theme.palette.divider}` 
          }}>
            <Box sx={{ 
              display: 'flex', 
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <Typography variant="caption" color="text.secondary">
                {t('listing.deposit', 'Caution')}
              </Typography>
              <Typography variant="caption" fontWeight={600}>
                {formatCurrency(listing.deposit_amount)}
              </Typography>
            </Box>
          </Box>
        )}
      </Box>
    );
  }

  // Mode par défaut (compact)
  return null;
};

export default PriceDisplay;
